// src/components/Sidebar.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import { logoutSession } from "../api";
import axios from "axios";
import { createPortal } from "react-dom";
import "../sass/global/sidebar.scss";
import { useNavigate, useLocation } from "react-router-dom";
import { showInfo, showError } from "../utils/toast";
import PontoReminderModal from "./PontoReminderModal";
import {
  FiChevronLeft,
  FiChevronRight,
  FiList,
  FiUser,
  FiLogOut,
  FiHome,
  FiClock,
  FiRepeat,
  FiAlertTriangle,
  FiCalendar,
  FiMessageSquare,
  FiUpload,
  FiDownload,
  FiHash,
  FiDollarSign,
  FiBarChart2,
  FiFileText,
  FiTool,
  FiMoreHorizontal,
  FiX,
} from "react-icons/fi";
import Logo from "../assets/logo-longo-branco.webp";
import LogoCurto from "../assets/logo-curto-branco.webp";
import { api } from "../api";

// ---- badges + avisos globais (mensagens/agenda)
import { useNotify } from "../context/notifyStore";

const BREAKPOINT_COLLAPSE = 1280;
const MOBILE_BREAKPOINT = 900;
const DEBUG_SIDEBAR = false;

// Upload: escolha a UX
const USE_MODAL = false; // false = upload rápido | true = modal

// --- ajustes de upload ---
const UPLOAD_TIMEOUT_MS = 120000; // 2 minutos
const AXIOS_UPLOAD_OPTS = {
  timeout: UPLOAD_TIMEOUT_MS,
  maxBodyLength: Infinity,
  maxContentLength: Infinity,
  withCredentials: true,
};

/** ===== API dos incidentes (card de erros do sidebar) ===== */
const INC_BASE = "https://apiincidentesgit3-production.up.railway.app";
const incHttp = axios.create({ baseURL: INC_BASE, timeout: 15000 });
const INC_ENDPOINT = "/incidentes";

/** ===== Lembrete forte de ponto ===== */
const PONTO_REMINDER_SNOOZE_MINUTES = 10;
const PONTO_REMINDER_CHECK_MS = 15_000;

const SIDEBAR_BLINK_CSS = `
@keyframes sbBlink { 0%,100%{opacity:1} 50%{opacity:.35} }
.sb-blink { animation: sbBlink .8s steps(2,end) infinite; will-change: opacity; }
`;

/** ===== Helpers ===== */
const todayISO = () => new Date().toISOString().slice(0, 10);
const pad2 = (n) => String(n).padStart(2, "0");

const makeQS = (obj = {}) =>
  Object.entries(obj)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(
      ([k, v]) =>
        `${encodeURIComponent(k)}=${encodeURIComponent(
          v instanceof Date ? v.toISOString() : v
        )}`
    )
    .join("&");

// mantém o /ssw do baseURL quando o path é relativo (não começa com "/")
const absURL = (path) => {
  const base = api?.defaults?.baseURL || window.location.origin;
  const u = new URL(base);
  const basePath = u.pathname.replace(/\/+$/, "");
  if (path.startsWith("/")) return `${u.origin}${path}`;
  return `${u.origin}${basePath ? basePath + "/" : "/"}${path}`;
};

const formatBytes = (n) => {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const formatWhen = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const dd = pad2(d.getDate());
    const mm = pad2(d.getMonth() + 1);
    const yyyy = d.getFullYear();
    const hh = pad2(d.getHours());
    const min = pad2(d.getMinutes());
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  } catch {
    return iso;
  }
};

const formatCampoLabel = (campo) =>
  String(campo || "")
    .replace(/([A-Z])/g, " $1")
    .trim();


const getServerDateParts = (dateOrMs, tzMinutes = 0) => {
  const ms =
    dateOrMs instanceof Date ? dateOrMs.getTime() : Number(dateOrMs || Date.now());
  const shifted = new Date(ms + Number(tzMinutes || 0) * 60 * 1000);

  return {
    d: pad2(shifted.getUTCDate()),
    m: pad2(shifted.getUTCMonth() + 1),
    y: String(shifted.getUTCFullYear()),
    hh: pad2(shifted.getUTCHours()),
    min: pad2(shifted.getUTCMinutes()),
    ss: pad2(shifted.getUTCSeconds()),
  };
};

const parseHHmmToMinutes = (hhmm) => {
  const s = String(hhmm || "").trim();
  if (!/^\d{2}:\d{2}$/.test(s)) return null;
  const [h, m] = s.split(":").map(Number);
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
};

const getReminderStorageKey = (codFunc) =>
  `sidebar:pontoReminder:${String(codFunc || "0")}`;

const readReminderState = (codFunc) => {
  try {
    return JSON.parse(
      localStorage.getItem(getReminderStorageKey(codFunc)) || "null"
    );
  } catch {
    return null;
  }
};

const writeReminderState = (codFunc, value) => {
  try {
    localStorage.setItem(getReminderStorageKey(codFunc), JSON.stringify(value));
  } catch {}
};

const clearReminderState = (codFunc) => {
  try {
    localStorage.removeItem(getReminderStorageKey(codFunc));
  } catch {}
};

const normalizeFlagSN = (value, defaultValue = "S") => {
  const normalized = String(value ?? defaultValue)
    .trim()
    .toUpperCase();

  return normalized === "N" ? "N" : "S";
};

const readSessionFlag = (keys = [], defaultValue = "S") => {
  for (const key of keys) {
    const raw = sessionStorage.getItem(key) ?? localStorage.getItem(key);
    if (raw !== null && raw !== undefined && String(raw).trim() !== "") {
      return normalizeFlagSN(raw, defaultValue);
    }
  }

  return normalizeFlagSN(defaultValue, defaultValue);
};

/** Popover/painel fora da sidebar (no <body>) */
function FloatSubmenu({
  open,
  anchorRef,
  onClose,
  children,
  width = 220,
  className = "",
  role = "menu",
  ariaLabel,
}) {
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 8, left: 8 });

  const place = useCallback(() => {
    const anchor = anchorRef?.current;
    if (!anchor) return;

    const anchorRect = anchor.getBoundingClientRect();
    const menu = menuRef.current;
    const menuWidth = menu?.offsetWidth || width;
    const menuHeight = menu?.offsetHeight || 0;
    const gap = 10;
    const viewportGap = 8;

    let left = anchorRect.right + gap;
    let top = anchorRect.top;

    if (left + menuWidth > window.innerWidth - viewportGap) {
      left = Math.max(viewportGap, anchorRect.left - menuWidth - gap);
    }

    if (menuHeight > 0) {
      const maxTop = window.innerHeight - menuHeight - viewportGap;
      top = Math.max(viewportGap, Math.min(top, maxTop));
    }

    setPos({ top, left });
  }, [anchorRef, width]);

  useLayoutEffect(() => {
    if (!open) return undefined;

    place();
    const raf = window.requestAnimationFrame(place);
    const onViewportChange = () => place();

    window.addEventListener("resize", onViewportChange);
    window.addEventListener("scroll", onViewportChange, true);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("scroll", onViewportChange, true);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    const onPointerDown = (e) => {
      if (!menuRef.current) return;

      if (
        !menuRef.current.contains(e.target) &&
        !anchorRef.current?.contains(e.target)
      ) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onPointerDown);

    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onPointerDown);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={menuRef}
      className={`pop-submenu ${className}`.trim()}
      style={{ top: pos.top, left: pos.left, width }}
      role={role}
      aria-label={ariaLabel}
    >
      {children}
    </div>,
    document.body
  );
}

/** ===== Modal de Upload de Release ===== */
function ReleaseUploadModal({
  open,
  onClose,
  onSuccess,
  produtoCfg,
  produtoRelease,
}) {
  const [file, setFile] = useState(null);
  const [versao, setVersao] = useState("");
  const [notas, setNotas] = useState("");
  const [autor, setAutor] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(null);
      setVersao("");
      setNotas("");
      setAutor(
        sessionStorage.getItem("RazaoSocial") ||
          sessionStorage.getItem("NomeFunc") ||
          sessionStorage.getItem("name") ||
          ""
      );
      setSubmitting(false);
    }
  }, [open]);

  const doSubmit = async (e) => {
    e?.preventDefault();
    if (submitting) return;

    const accept = String(produtoCfg?.accept || "").toLowerCase();

    const err = (() => {
      if (!file) return "Selecione um arquivo.";
      const lower = String(file.name || "").toLowerCase();
      if (accept === ".exe" && !lower.endsWith(".exe"))
        return "Somente .exe é permitido.";
      if (accept === ".dll" && !lower.endsWith(".dll"))
        return "Somente .dll é permitido.";
      if (!versao.trim()) return "Informe a versão.";
      if (!notas.trim() || notas.trim().length < 3)
        return "Informe o que a atualização contempla.";
      if (!autor.trim()) return "Informe quem está subindo.";
      return null;
    })();
    if (err) return showError(err);

    const nome =
      window.prompt("Nome do arquivo (ex.: SPW_VERSAO_001)", "") || "";
    if (!nome.trim()) {
      showError("Informe o nome do arquivo.");
      return;
    }

    const fd = new FormData();
    fd.append("file", file);
    fd.append("versao", versao.trim());
    fd.append("notas", notas.trim());
    fd.append("autor", autor.trim());
    fd.append("nome", nome.trim());
    if (produtoRelease) fd.append("produto", produtoRelease);

    try {
      setSubmitting(true);
      await api.post("ssw/releases/upload", fd, {
        ...AXIOS_UPLOAD_OPTS,
        onUploadProgress: () => {},
        headers: { "Content-Type": "multipart/form-data" },
      });
      showInfo("Release enviado para testes.");
      onSuccess?.();
      onClose?.();
    } catch (e2) {
      const aborted = e2?.code === "ECONNABORTED";
      const msg =
        (aborted
          ? `Tempo excedido (${UPLOAD_TIMEOUT_MS / 1000}s).`
          : e2?.response?.data?.error) ||
        e2?.message ||
        "Falha ao publicar release.";
      showError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal upload-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Upload de Release"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>
            <FiUpload style={{ marginRight: 8 }} />
            Enviar novo release (.EXE)
          </h3>
          <button className="close-btn" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </div>
        <form className="modal-body" onSubmit={doSubmit}>
          <label className="field">
            <span>Arquivo (.exe) *</span>
            <input
              type="file"
              accept={produtoCfg?.accept || "*"}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              required
            />
          </label>

          <label className="field">
            <span>Versão *</span>
            <input
              type="text"
              placeholder="Ex.: 3.2.15"
              value={versao}
              onChange={(e) => setVersao(e.target.value)}
              required
            />
          </label>

          <label className="field">
            <span>O que contempla *</span>
            <textarea
              rows={3}
              placeholder="Correções X,Y; melhoria Z"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              required
            />
          </label>

          <label className="field">
            <span>Quem subiu *</span>
            <input
              type="text"
              value={autor}
              onChange={(e) => setAutor(e.target.value)}
              required
            />
          </label>

          <div className="modal-footer">
            <button type="button" className="btn secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn primary" disabled={submitting}>
              {submitting ? "Enviando..." : "Enviar para testes"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}



export default function Sidebar({ onToggle }) {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    mensagens: msgBadgeCtx = 0,
    agenda: agendaBadgeCtx = 0,
    clearBadge: clearNotifyBadge = () => {},
    avisos = [],
    markAvisoAsRead = () => {},
  } = useNotify();

  const avisosMensagens = useMemo(
    () => (avisos || []).filter((a) => a?.tipo === "MSG_NOTIFY" && !a?.lido),
    [avisos]
  );
  const avisosAgenda = useMemo(
    () =>
      (avisos || []).filter((a) => a?.tipo === "AGENDA_REMINDER" && !a?.lido),
    [avisos]
  );
  const totalMensagensPend = avisosMensagens.length;
  const totalAgendaPend = avisosAgenda.length;

  const handleVerMensagens = useCallback(() => {
    avisosMensagens.forEach((a) => markAvisoAsRead(a.id));
    clearNotifyBadge("mensagens");
    navigate("/mensagens-internas");
  }, [avisosMensagens, markAvisoAsRead, clearNotifyBadge, navigate]);

  const handleVerAgenda = useCallback(() => {
    avisosAgenda.forEach((a) => markAvisoAsRead(a.id));
    clearNotifyBadge("agenda");
    navigate("/agenda");
  }, [avisosAgenda, markAvisoAsRead, clearNotifyBadge, navigate]);

  const handleDescartarMensagens = useCallback(() => {
    avisosMensagens.forEach((a) => markAvisoAsRead(a.id));
    clearNotifyBadge("mensagens");
  }, [avisosMensagens, markAvisoAsRead, clearNotifyBadge]);

  const handleDescartarAgenda = useCallback(() => {
    avisosAgenda.forEach((a) => markAvisoAsRead(a.id));
    clearNotifyBadge("agenda");
  }, [avisosAgenda, markAvisoAsRead, clearNotifyBadge]);

  useEffect(() => {
    if (location.pathname.startsWith("/mensagens-internas")) {
      clearNotifyBadge("mensagens");
      avisosMensagens.forEach((a) => markAvisoAsRead(a.id));
    }
    if (location.pathname.startsWith("/agenda")) {
      clearNotifyBadge("agenda");
      avisosAgenda.forEach((a) => markAvisoAsRead(a.id));
    }
  }, [
    location.pathname,
    clearNotifyBadge,
    avisosMensagens,
    avisosAgenda,
    markAvisoAsRead,
  ]);

  const styleOnceRef = useRef(false);

  const [autoCollapsed, setAutoCollapsed] = useState(
    typeof window !== "undefined"
      ? window.innerWidth < BREAKPOINT_COLLAPSE
      : false
  );
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined"
      ? window.innerWidth <= MOBILE_BREAKPOINT
      : false
  );
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [userCollapsed, setUserCollapsed] = useState(false);
  const isOpen = !autoCollapsed && !userCollapsed;
  const lockCollapsed = autoCollapsed;

  const [filaTotal, setFilaTotal] = useState(null);

  const cadAnchorRef = useRef(null);
  const projectsAnchorRef = useRef(null);
  const fiscalAnchorRef = useRef(null);
  const finAnchorRef = useRef(null);
  const communicationAnchorRef = useRef(null);
  const adminAnchorRef = useRef(null);

  const registrandoRef = useRef(false);
  const [registrandoCampo, setRegistrandoCampo] = useState(null);
  const [submenuOpen, setSubmenuOpen] = useState(null);

  // --- dados ponto/horários
  const [horarios, setHorarios] = useState(null);
  const [pontoDia, setPontoDia] = useState(null);
  const [dadosCarregados, setDadosCarregados] = useState(false);

  const [showPontoReminderModal, setShowPontoReminderModal] = useState(false);
  const [pontoReminderTick, setPontoReminderTick] = useState(Date.now());

  /* relógio do servidor */
  const [serverClock, setServerClock] = useState({
    now: null,
    skewMs: 0,
    lastSync: null,
    healthy: false,
    tzMinutes: 0,
    source: null,
  });

  const [resumoErros, setResumoErros] = useState({
    abertos: 0,
    emAtendimento: 0,
    finalizados: 0,
    total: 0,
  });
  const [errBadgeHidden, setErrBadgeHidden] = useState(() => {
    return localStorage.getItem("sidebar:err:hide") === "1";
  });

  const [projBadge, setProjBadge] = useState(0);
  const [projBlink, setProjBlink] = useState(false);
  const [projBaseline, setProjBaseline] = useState(
    () => localStorage.getItem(`sidebar:projSeen:${todayISO()}`) || ""
  );

  const [agBadge, setAgBadge] = useState(0);
  const [agBlink, setAgBlink] = useState(false);
  const [agSinceId, setAgSinceId] = useState(0);
  const [agBaseline, setAgBaseline] = useState(
    () =>
      localStorage.getItem(
        `sidebar:agSeen:${
          sessionStorage.getItem("codFunc") || "0"
        }:${todayISO()}`
      ) || ""
  );
  const [agBlinkMuted, setAgBlinkMuted] = useState(() => {
    return (
      localStorage.getItem(
        `sidebar:agBlinkOff:${
          sessionStorage.getItem("codFunc") || "0"
        }:${todayISO()}`
      ) === "1"
    );
  });

  const getUserNameFromStorage = () => {
    try {
      const get = (k) =>
        (sessionStorage.getItem(k) ?? localStorage.getItem(k) ?? "").trim();
      const direct = get("RazaoSocial") || get("NomeFunc") || get("name");
      if (direct) return direct;
      const parse = (raw) => {
        try {
          return JSON.parse(raw || "{}");
        } catch {
          return {};
        }
      };
      const u = {
        ...parse(sessionStorage.getItem("user")),
        ...parse(localStorage.getItem("user")),
      };
      return (u.RazaoSocial || u.nome || u.name || u.NomeFunc || "")
        .toString()
        .trim();
    } catch {
      return "";
    }
  };

  const [userName, setUserName] = useState(getUserNameFromStorage());

  // session
  const codFunc = sessionStorage.getItem("codFunc");
  const codCadFunc = sessionStorage.getItem("CodCadFunc");
  const idFuncionario = Number(sessionStorage.getItem("IdFuncionario"));

  const dmControlaPonto = useMemo(
    () =>
      readSessionFlag(
        ["dmControlaPonto", "DM_CONTROLA_PONTO", "controlaPonto"],
        "S"
      ),
    []
  );

  const dmExibeLembretePonto = useMemo(
    () =>
      readSessionFlag(
        [
          "dmExibeLembretePonto",
          "DM_EXIBE_LEMBRETE_PONTO",
          "exibeLembretePonto",
        ],
        "S"
      ),
    []
  );

  const podeExibirLembretePonto =
    dmControlaPonto === "S" && dmExibeLembretePonto === "S";

  const isADM = useMemo(
    () =>
      String(sessionStorage.getItem("NivelFunc") || "").toUpperCase() === "ADM",
    []
  );
  const isMarketing = useMemo(
    () =>
      String(sessionStorage.getItem("Cargo") || "").toUpperCase() ===
      "MARKETING",
    []
  );
  const isFinanceiro = useMemo(
    () =>
      String(sessionStorage.getItem("Setor") || "").toUpperCase() ===
      "FINANCEIRO",
    []
  );

  const isEzequiel = useMemo(() => {
    const idFunc =
      sessionStorage.getItem("IdFuncionario") ||
      localStorage.getItem("IdFuncionario") ||
      "";
    const cod =
      sessionStorage.getItem("codFunc") ||
      localStorage.getItem("codFunc") ||
      "";
    return String(idFunc).trim() === "5" || String(cod).trim() === "13";
  }, []);

  const canSeeFinanceiro = isADM || isFinanceiro || isEzequiel;
  const canSeeCobranca = isFinanceiro || isEzequiel;
  const canSeeContasPagar = isFinanceiro || isEzequiel;
  const canSeeDashboardWhatsApp = isADM || isFinanceiro || isEzequiel;
  const canSeeFiscalNfe = isADM || isFinanceiro || isEzequiel;

  const getDevId = useCallback(() => {
    const names = [
      "DevId",
      "devId",
      "DEV_ID",
      "userId",
      "id",
      "CodCadFunc",
      "codFunc",
      "IdFuncionario",
      "idFuncionario",
    ];
    for (const name of names) {
      const raw = sessionStorage.getItem(name);
      const n = Number(raw);
      if (Number.isInteger(n) && n > 0) return n;
    }
    return 0;
  }, []);

  const devId = getDevId();
  if (DEBUG_SIDEBAR) console.info("[SB] devId:", devId);

  const loadFilaTotal = useCallback(async () => {
    if (!devId) return setFilaTotal(null);
    try {
      const { data } = await api.get("ssw/projetos", {
        params: { mine: true, devId, limit: 1, page: 1, openOnly: 1 },
      });
      setFilaTotal(Number(data?.total ?? 0));
    } catch {
      setFilaTotal(null);
    }
  }, [devId]);

  useEffect(() => {
    let int;
    (async () => {
      await loadFilaTotal();
      int = setInterval(loadFilaTotal, 60_000);
    })();
    return () => clearInterval(int);
  }, [loadFilaTotal]);

  const [zNumero, setZNumero] = useState("");
  const excelSerial = (d) =>
    Math.floor(
      (Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) -
        Date.UTC(1899, 11, 30)) /
        86400000
    );
  const toZNumber = (serial) => `Z${String(serial).slice(1)}`;
  const updateZNumber = useCallback(() => {
    const serial = excelSerial(new Date());
    setZNumero(toZNumber(serial));
  }, []);
  useEffect(() => {
    updateZNumber();
    const id = setInterval(updateZNumber, 60 * 60 * 1000);
    return () => clearInterval(id);
  }, [updateZNumber]);

  useEffect(() => {
    const onResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= MOBILE_BREAKPOINT);
      setAutoCollapsed(width < BREAKPOINT_COLLAPSE);
    };

    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    onToggle?.(isMobile ? false : isOpen);
  }, [isMobile, isOpen, onToggle]);

  useEffect(() => {
    if (!isMobile) {
      setMobileMoreOpen(false);
      return;
    }

    const previousOverflow = document.body.style.overflow;

    if (mobileMoreOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobile, mobileMoreOpen]);

  useEffect(() => {
    setMobileMoreOpen(false);
  }, [location.pathname]);

  const toggleSidebar = () => {
    if (lockCollapsed) return;
    setUserCollapsed((v) => !v);
    setSubmenuOpen(null);
  };

  const handleLogout = async () => {
    await logoutSession();
    navigate("/", { replace: true });
  };

  const goReleaseTestes = () => {
    setReleasePanelOpen(false);
    navigate("/releases/testes");
  };

  useEffect(() => {
    const onStorage = (e) => {
      if (["RazaoSocial", "NomeFunc", "name", "user"].includes(e.key)) {
        setUserName(getUserNameFromStorage());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  /* ===== relógio do servidor ===== */
  const syncServerTime = useCallback(async () => {
    try {
      const { data } = await api.get("/ponto/server-time");

      const serverUtcMs =
        (Number.isFinite(data?.epochMsUtc) && data.epochMsUtc) ||
        (data?.serverUTCISO && Date.parse(data.serverUTCISO)) ||
        (data?.serverOffsetISO && Date.parse(data.serverOffsetISO)) ||
        null;

      if (!serverUtcMs) throw new Error("payload inesperado");

      setServerClock({
        now: new Date(serverUtcMs),
        skewMs: serverUtcMs - Date.now(),
        lastSync: new Date(),
        healthy: true,
        tzMinutes: Number(data?.offsetMinutes) || 0,
        source: data?.source || "sqlserver",
      });
    } catch (e) {
      console.warn("Falha ao sincronizar hora do servidor.", e);
      setServerClock((s) => ({
        ...s,
        now: new Date(),
        skewMs: 0,
        lastSync: new Date(),
        healthy: false,
      }));
    }
  }, []);

  useEffect(() => {
    syncServerTime();

    const tick = setInterval(() => {
      setServerClock((s) =>
        s.now ? { ...s, now: new Date(s.now.getTime() + 1000) } : s
      );
    }, 1000);

    const resync = setInterval(syncServerTime, 60_000);

    return () => {
      clearInterval(tick);
      clearInterval(resync);
    };
  }, [syncServerTime]);

  // ------ dados ponto ------
  const buscarHorarios = useCallback(async () => {
    if (!codFunc || !podeExibirLembretePonto) {
      setHorarios(null);
      return;
    }

    try {
      if (Number.isFinite(idFuncionario) && idFuncionario > 0) {
        const res = await api.get(`/func-horario/func/${idFuncionario}/horario`);
        setHorarios(res?.data || null);
        return;
      }
    } catch (e) {
      console.warn("Falha ao buscar horário pela rota nova. Tentando fallback.", e);
    }

    try {
      const res = await api.get(`/inform-func/${codFunc}`);
      setHorarios(res?.data || null);
    } catch {
      setHorarios(null);
    }
  }, [codFunc, idFuncionario, podeExibirLembretePonto]);

  const buscarPontoDia = useCallback(async () => {
    if (!codFunc || !podeExibirLembretePonto) {
      setPontoDia(null);
      return;
    }

    const ref = getServerDateParts(serverClock.now || new Date(), serverClock.tzMinutes || 0);

    try {
      const res = await api.get(`/ponto/${codFunc}?mes=${ref.m}&ano=${ref.y}&limit=100`);
      const lista = res?.data?.data || [];

      const pontoHoje = lista.find((p) => {
        const d = pad2(Number(p.Dia));
        const m = pad2(Number(p.Mes));
        const a = String(p.Ano).trim();
        return d === ref.d && m === ref.m && a === ref.y;
      });

      setPontoDia(pontoHoje ? pontoHoje : { __vazio: true });
    } catch {
      setPontoDia({ __erro: true });
    }
  }, [codFunc, podeExibirLembretePonto, serverClock.now, serverClock.tzMinutes]);

  useEffect(() => {
    let cancelled = false;

    if (!podeExibirLembretePonto) {
      setHorarios(null);
      setPontoDia(null);
      setDadosCarregados(false);
      setShowPontoReminderModal(false);
      if (codFunc) clearReminderState(codFunc);
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        await Promise.all([buscarHorarios(), buscarPontoDia()]);
      } finally {
        if (!cancelled) setDadosCarregados(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [buscarHorarios, buscarPontoDia, podeExibirLembretePonto, codFunc]);

  useEffect(() => {
    if (!podeExibirLembretePonto) return undefined;

    const id = setInterval(() => {
      buscarPontoDia();
    }, 60_000);

    return () => clearInterval(id);
  }, [buscarPontoDia, podeExibirLembretePonto]);

  // ------ alertas de ponto ------
  const alertas = useMemo(() => {
    if (
      !podeExibirLembretePonto ||
      !dadosCarregados ||
      !horarios ||
      !pontoDia ||
      pontoDia.__erro
    ) {
      return [];
    }

    const ref = getServerDateParts(
      serverClock.now || new Date(),
      serverClock.tzMinutes || 0
    );
    const agoraMin = Number(ref.hh) * 60 + Number(ref.min);

    const passou = (hhmm) => {
      const alvoMin = parseHHmmToMinutes(hhmm);
      if (alvoMin == null) return false;
      return agoraMin >= alvoMin;
    };

    const out = [];

    if (
      horarios.EntradaManha &&
      passou(horarios.EntradaManha) &&
      !pontoDia.EntradaManha
    ) {
      out.push({
        label: "Entrada da Manhã",
        campo: "EntradaManha",
        previsto: horarios.EntradaManha,
      });
    }

    if (
      horarios.SaidaManha &&
      passou(horarios.SaidaManha) &&
      !pontoDia.SaidaManha &&
      pontoDia.EntradaManha
    ) {
      out.push({
        label: "Saída para Almoço",
        campo: "SaidaManha",
        previsto: horarios.SaidaManha,
      });
    }

    if (
      horarios.EntradaTarde &&
      passou(horarios.EntradaTarde) &&
      !pontoDia.EntradaTarde &&
      pontoDia.SaidaManha
    ) {
      out.push({
        label: "Retorno do Almoço",
        campo: "EntradaTarde",
        previsto: horarios.EntradaTarde,
      });
    }

    if (
      horarios.SaidaTarde &&
      passou(horarios.SaidaTarde) &&
      !pontoDia.SaidaTarde &&
      pontoDia.EntradaTarde
    ) {
      out.push({
        label: "Saída da Tarde",
        campo: "SaidaTarde",
        previsto: horarios.SaidaTarde,
      });
    }

    return out;
  }, [
    podeExibirLembretePonto,
    dadosCarregados,
    horarios,
    pontoDia,
    serverClock.now,
    serverClock.tzMinutes,
  ]);

  const alertaPrioritario = useMemo(() => {
    return alertas.length > 0 ? alertas[0] : null;
  }, [alertas]);

  useEffect(() => {
    if (!podeExibirLembretePonto) return undefined;

    const id = setInterval(() => {
      setPontoReminderTick(Date.now());
    }, PONTO_REMINDER_CHECK_MS);

    return () => clearInterval(id);
  }, [podeExibirLembretePonto]);

  useEffect(() => {
    if (!codFunc || !podeExibirLembretePonto) {
      setShowPontoReminderModal(false);
      if (codFunc) clearReminderState(codFunc);
      return;
    }

    if (!alertaPrioritario) {
      setShowPontoReminderModal(false);
      clearReminderState(codFunc);
      return;
    }

    const now = Date.now();
    const saved = readReminderState(codFunc);
    const currentCampo = alertaPrioritario.campo;

    if (!saved || saved.campo !== currentCampo) {
      writeReminderState(codFunc, {
        campo: currentCampo,
        nextOpenAt: now,
      });
      setShowPontoReminderModal(true);
      return;
    }

    if (showPontoReminderModal) return;

    if (now >= Number(saved.nextOpenAt || 0)) {
      setShowPontoReminderModal(true);
    }
  }, [
    codFunc,
    podeExibirLembretePonto,
    alertaPrioritario,
    pontoReminderTick,
    showPontoReminderModal,
  ]);

  const handleClosePontoReminder = useCallback(() => {
    setShowPontoReminderModal(false);
  }, []);

  const handleRemindLaterPontoReminder = useCallback(() => {
    if (codFunc && alertaPrioritario) {
      writeReminderState(codFunc, {
        campo: alertaPrioritario.campo,
        nextOpenAt:
          Date.now() + PONTO_REMINDER_SNOOZE_MINUTES * 60 * 1000,
      });
    }

    setShowPontoReminderModal(false);
  }, [codFunc, alertaPrioritario]);

  const handleRegistrarPonto = async (campo) => {
    if (!codFunc) {
      showError("Funcionário inválido para registro de ponto.");
      return;
    }

    if (!pontoDia || pontoDia.__erro) {
      showError("Erro ao obter dados de ponto.");
      return;
    }

    if (registrandoRef.current) return;
    registrandoRef.current = true;
    setRegistrandoCampo(campo || "batida");

    const payload = {
      NomeFunc: sessionStorage.getItem("RazaoSocial") || "Funcionário",
      CodFunc: Number(codFunc),
      CodCadFunc: codCadFunc || "",
      campo,
      strictHorario: false,
    };

    try {
      const { data } = await api.post("/ponto/bater", payload);

      const campoGravado = data?.campoGravado;
      const foraJanela = data?.horarioValidacao?.foraDaJanela;

      if (campoGravado) {
        const baseMsg = `${formatCampoLabel(campoGravado)} registrado com sucesso.`;
        showInfo(foraJanela ? `${baseMsg} Fora da janela prevista.` : baseMsg);
      } else {
        showInfo("Ponto registrado com sucesso!");
      }

      setShowPontoReminderModal(false);
      clearReminderState(codFunc);

      await buscarPontoDia();
      await syncServerTime();
    } catch (e) {
      const msg =
        e?.response?.data?.mensagem ||
        e?.response?.data?.message ||
        "Erro ao registrar ponto.";
      showError(msg);
    } finally {
      setRegistrandoCampo(null);
      registrandoRef.current = false;
    }
  };

  /** ===== Resumo de erros ===== */
  const carregarResumoErros = useCallback(async (signal) => {
    try {
      const { data } = await incHttp.get(`${INC_ENDPOINT}/resumo`, { signal });
      const safe = {
        abertos: Number(data?.abertos ?? 0),
        emAtendimento: Number(data?.emAtendimento ?? 0),
        finalizados: Number(data?.finalizados ?? 0),
        total: Number(data?.total ?? 0),
      };
      setResumoErros(safe);
    } catch {
      /* mantém último valor */
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    carregarResumoErros(ctrl.signal);
    const id = setInterval(() => carregarResumoErros(ctrl.signal), 60_000);
    return () => {
      ctrl.abort();
      clearInterval(id);
    };
  }, [carregarResumoErros]);

  // ======== AGENDAMENTOS: SSE + Poll =========
  const agStreamRef = useRef(null);
  const agPollRef = useRef(null);

  const primeAgNow = useCallback(async () => {
    if (!codFunc) return { maxId: 0 };
    try {
      const { data } = await api.get("/ssw/agendamentos/novidades", {
        params: { codFunc, sinceId: 0, includeItens: false, limit: 1 },
      });
      return { maxId: Number(data?.maxId || 0) };
    } catch {
      return { maxId: 0 };
    }
  }, [codFunc]);

  const doAgCheckOnce = useCallback(
    async (since) => {
      if (!codFunc) return;
      try {
        const { data } = await api.get("/ssw/agendamentos/novidades", {
          params: {
            codFunc,
            sinceId: since || 0,
            includeItens: true,
            limit: 10,
          },
        });
        const itens = Array.isArray(data?.itens) ? data.itens : [];
        const onlyToday = itens.filter((i) => i.DataISO === todayISO());
        const newToday = onlyToday.length;
        if (newToday > 0) {
          setAgBadge(newToday);
          if (agBaseline && !agBlinkMuted) setAgBlink(true);
        }
        setAgSinceId((s) => Math.max(s, Number(data?.maxId || s || 0)));
      } catch {}
    },
    [codFunc, agBaseline, agBlinkMuted]
  );

  const startAgPolling = useCallback(
    (since) => {
      if (!codFunc) return;
      if (agPollRef.current) clearInterval(agPollRef.current);
      agPollRef.current = setInterval(async () => {
        await doAgCheckOnce(since || 0);
      }, 8_000);
    },
    [codFunc, doAgCheckOnce]
  );

  const startAgSSE = useCallback(
    async (since) => {
      if (!codFunc) return;
      if (agStreamRef.current) {
        try {
          agStreamRef.current.close();
        } catch {}
      }
      const qs = makeQS({ codFunc, sinceId: since || 0 });
      const url = absURL(`ssw/agendamentos/stream?${qs}`);
      const es = new EventSource(url);
      agStreamRef.current = es;

      es.addEventListener("prime", (ev) => {
        try {
          const p = JSON.parse(ev.data || "{}");
          const maxId = Number(p?.maxId || 0);
          if (maxId) setAgSinceId(maxId);
        } catch {}
      });
      es.addEventListener("novidades", (ev) => {
        try {
          const r = JSON.parse(ev.data || "{}");
          const itens = Array.isArray(r?.itens) ? r.itens : [];
          const onlyToday = itens.filter((i) => i.DataISO === todayISO());
          const newToday = onlyToday.length;
          if (newToday > 0) {
            setAgBadge(newToday);
            if (agBaseline && !agBlinkMuted) setAgBlink(true);
          }
          setAgSinceId((s) => Math.max(s, Number(r?.maxId || s || 0)));
        } catch {}
      });
      es.addEventListener("error", () => {
        try {
          es.close();
        } catch {}
        startAgPolling(since || 0);
      });

      await doAgCheckOnce(since || 0);
    },
    [codFunc, startAgPolling, doAgCheckOnce, agBaseline, agBlinkMuted]
  );

  useEffect(() => {
    const onAgPage = location.pathname.startsWith("/agendamentos");
    (async () => {
      if (onAgPage && codFunc) {
        const p = await primeAgNow();
        const key = `sidebar:agSeen:${codFunc}:${todayISO()}`;
        localStorage.setItem(key, String(p.maxId || 0));
        setAgBaseline(String(p.maxId || 0));
        setAgBlink(false);
        setAgBadge(0);
      }
    })();
  }, [location.pathname, codFunc, primeAgNow]);

  useEffect(() => {
    if (!codFunc) return;
    const since = Number(
      localStorage.getItem(`sidebar:agSeen:${codFunc}:${todayISO()}`) || 0
    );
    startAgSSE(since);
    return () => {
      try {
        agStreamRef.current?.close();
      } catch {}
      if (agPollRef.current) clearInterval(agPollRef.current);
    };
  }, [codFunc, startAgSSE]);

  const ackAgToday = useCallback(() => {
    if (!codFunc) return;
    const key = `sidebar:agSeen:${codFunc}:${todayISO()}`;
    const seen = String(agSinceId || 0);
    localStorage.setItem(key, seen);
    setAgBaseline(seen);
    setAgBadge(0);
    setAgBlink(false);
  }, [codFunc, agSinceId]);

  const toggleAgBlink = useCallback(() => {
    if (!codFunc) return;
    const key = `sidebar:agBlinkOff:${codFunc}:${todayISO()}`;
    const next = !agBlinkMuted;
    localStorage.setItem(key, next ? "1" : "0");
    setAgBlinkMuted(next);
    if (next) setAgBlink(false);
  }, [codFunc, agBlinkMuted]);

  // ======== PROJETOS: SSE + Poll ========
  const projStreamRef = useRef(null);
  const projPollRef = useRef(null);

  const doProjCheckOnce = useCallback(
    async (sinceAtISO) => {
      if (!devId) return;
      try {
        const { data } = await api.get("ssw/projetos/novidades", {
          params: {
            devId,
            sinceAt: sinceAtISO || null,
            includeItens: true,
            limit: 10,
          },
        });
        const totalNew = Number(data?.totalNew || 0);
        if (totalNew > 0) {
          setProjBadge(totalNew);
          if (projBaseline) setProjBlink(true);
        }
      } catch (e) {
        if (DEBUG_SIDEBAR) console.warn("[SB] proj novidades err:", e?.message);
      }
    },
    [devId, projBaseline]
  );

  const startProjPolling = useCallback(
    (sinceAtISO) => {
      if (!devId) return;
      if (projPollRef.current) clearInterval(projPollRef.current);
      projPollRef.current = setInterval(async () => {
        await doProjCheckOnce(sinceAtISO || null);
      }, 20_000);
    },
    [devId, doProjCheckOnce]
  );

  const startProjSSE = useCallback(
    async (sinceAtISO) => {
      await loadFilaTotal();

      if (!devId) return;
      if (projStreamRef.current) {
        try {
          projStreamRef.current.close();
        } catch {}
      }
      const qs = makeQS({ devId, sinceAt: sinceAtISO || "" });
      const url = absURL(`ssw/projetos/stream?${qs}`);
      const es = new EventSource(url);
      projStreamRef.current = es;

      es.addEventListener("prime", () => {});
      es.addEventListener("novidades", (ev) => {
        try {
          const r = JSON.parse(ev.data || "{}");
          const totalNew = Number(r?.totalNew || 0);
          if (totalNew > 0) {
            setProjBadge(totalNew);
            loadFilaTotal();
            if (projBaseline) setProjBlink(true);
          }
        } catch {}
      });
      es.addEventListener("error", () => {
        try {
          es.close();
        } catch {}
        startProjPolling(sinceAtISO || null);
      });

      await doProjCheckOnce(sinceAtISO || null);
    },
    [devId, startProjPolling, doProjCheckOnce, projBaseline, loadFilaTotal]
  );

  useEffect(() => {
    const onMe = location.pathname.startsWith("/minhaFila");
    const onProj = location.pathname.startsWith("/projetos");
    (async () => {
      if ((onMe || onProj) && devId) {
        const seen = new Date().toISOString();
        localStorage.setItem(`sidebar:projSeen:${todayISO()}`, seen);
        setProjBaseline(seen);
        setProjBadge(0);
        setProjBlink(false);
      }
    })();
  }, [location.pathname, devId]);

  useEffect(() => {
    if (!devId) return;
    const sinceAtISO =
      localStorage.getItem(`sidebar:projSeen:${todayISO()}`) || null;
    startProjSSE(sinceAtISO);
    return () => {
      try {
        projStreamRef.current?.close();
      } catch {}
      if (projPollRef.current) clearInterval(projPollRef.current);
    };
  }, [devId, startProjSSE]);

  // helpers
 // const isActive = (path) => location.pathname.startsWith(path);
  const goErros = () => navigate("/erros");

  const toggleErrVisibility = () => {
    const next = !errBadgeHidden;
    setErrBadgeHidden(next);
    localStorage.setItem("sidebar:err:hide", next ? "1" : "0");
  };

  useEffect(() => {
    if (styleOnceRef.current) return;
    styleOnceRef.current = true;
    const tag = document.createElement("style");
    tag.setAttribute("data-sb", "blink");
    tag.innerHTML = SIDEBAR_BLINK_CSS;
    document.head.appendChild(tag);
    return () => {
      try {
        document.head.removeChild(tag);
      } catch {}
    };
  }, []);


  /** ================= RELEASES (.EXE) ================= */
  const [releaseLast, setReleaseLast] = useState(null);
  const [relLoading, setRelLoading] = useState(true);
  const [relErr, setRelErr] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [releasePanelOpen, setReleasePanelOpen] = useState(false);
  const releaseAnchorRef = useRef(null);

  useEffect(() => {
    setReleasePanelOpen(false);
  }, [location.pathname]);

  const RELEASE_PRODUTOS = [
    { key: "SPW", label: "SPW (EXE)", accept: ".exe" },
    { key: "VB6", label: "VB6 (EXE)", accept: ".exe" },
    { key: "DLL", label: "DLL", accept: ".dll" },
  ];

  const getProdutoDefault = () =>
    localStorage.getItem("releases:produto") || "SPW";

  const [produtoRelease, setProdutoRelease] = useState(getProdutoDefault());

  useEffect(() => {
    localStorage.setItem("releases:produto", produtoRelease);
  }, [produtoRelease]);

  const produtoCfg =
    RELEASE_PRODUTOS.find((p) => p.key === produtoRelease) ||
    RELEASE_PRODUTOS[0];

  const lastStatus = String(
    releaseLast?.status || releaseLast?.STATUS || ""
  ).toUpperCase();

  const lastAtiva = !!(releaseLast?.ativa ?? releaseLast?.ATIVA);

  const releaseVersao =
    releaseLast?.versao ?? releaseLast?.VERSAO ?? "";
  const releaseAutor = releaseLast?.autor ?? releaseLast?.AUTOR ?? "";
  const releaseUploadedAt =
    releaseLast?.uploaded_at ?? releaseLast?.UPLOADED_AT ?? null;
  const releaseFileSize =
    releaseLast?.file_size ?? releaseLast?.FILE_SIZE ?? null;
  const releaseSha256 = releaseLast?.sha256 ?? releaseLast?.SHA256 ?? "";

  const releaseStatusClass = lastAtiva
    ? "production"
    : lastStatus === "APROVADO"
    ? "approved"
    : "testing";

  const releaseStatusLabel = lastAtiva
    ? "Produção"
    : lastStatus === "APROVADO"
    ? "Aprovado"
    : lastStatus || "Em teste";

  const releaseOnline = !relLoading && !relErr && !!releaseLast;

  const releaseConnectionClass = relLoading
    ? "checking"
    : releaseOnline
    ? "online"
    : "offline";

  const releaseConnectionLabel = relLoading
    ? "Verificando disponibilidade"
    : releaseOnline
    ? "Serviço de releases online"
    : "Serviço de releases offline";

  const releaseVersionText = (() => {
    if (relLoading) return `${produtoRelease} - Carregando release...`;
    if (relErr) return `${produtoRelease} - Serviço indisponível`;
    if (!releaseLast) return `${produtoRelease} - Nenhum release disponível`;

    const versao = String(releaseVersao || "").trim();

    if (!versao) return `${produtoRelease} - Sem versão`;
    if (/^vs\b/i.test(versao)) return `${produtoRelease} - ${versao}`;

    return `${produtoRelease} - VS ${versao}`;
  })();

  const fetchLatestRelease = useCallback(async () => {
    try {
      setRelLoading(true);
      const { data } = await api.get("ssw/releases/latest", {
        params: { produto: produtoRelease },
      });

      setReleaseLast(data?.last || null);
      setRelErr("");
    } catch (err) {
      console.error(
        "[SB][releases/latest] ERRO",
        err?.response?.status,
        err?.response?.data,
        err
      );
      setReleaseLast(null);
      setRelErr(
        err?.response?.data?.error ||
          `Falha ao carregar release (HTTP ${err?.response?.status || "?"}).`
      );
    } finally {
      setRelLoading(false);
    }
  }, [produtoRelease]);

  useEffect(() => {
    fetchLatestRelease();
    const id = setInterval(fetchLatestRelease, 60_000);
    return () => clearInterval(id);
  }, [fetchLatestRelease]);

  const openDownload = () => {
    const qs = makeQS({ produto: produtoRelease });
    window.open(absURL(`ssw/releases/download?${qs}`), "_blank", "noopener");
  };

  // ===== Upload rápido =====
  const quickFileRef = useRef(null);
  const [quickUploading, setQuickUploading] = useState(false);
  const [quickProgress, setQuickProgress] = useState(0);

  const getAutorPadrao = () =>
    sessionStorage.getItem("RazaoSocial") ||
    sessionStorage.getItem("NomeFunc") ||
    sessionStorage.getItem("name") ||
    "";

  const handleQuickFilePick = () => quickFileRef.current?.click();

  const parseVersaoFromName = (name = "") => {
    const m = name.match(/(\d+\.\d+(?:\.\d+)?)/);
    return m?.[1] || "";
  };

  const doQuickUpload = async (file) => {
    if (!file) return;
    const lower = String(file.name || "").toLowerCase();

    if (produtoCfg.accept === ".exe" && !lower.endsWith(".exe")) {
      showError("Arquivo inválido. Esperado .exe.");
      return;
    }
    if (produtoCfg.accept === ".dll" && !lower.endsWith(".dll")) {
      showError("Arquivo inválido. Esperado .dll.");
      return;
    }

    const autor = getAutorPadrao();
    let versao = parseVersaoFromName(file.name);
    if (!versao)
      versao = window.prompt("Versão deste release? (ex.: 3.2.15)", "") || "";

    if (!versao.trim()) {
      showError("Informe a versão para continuar.");
      return;
    }

    const fd = new FormData();
    fd.append("file", file);
    fd.append("versao", versao.trim());
    fd.append("notas", "-");
    fd.append("autor", autor);
    fd.append("produto", produtoRelease);

    const nome =
      window.prompt("Nome do arquivo (ex.: SPW_VERSAO_001)", "") || "";
    if (!nome.trim()) {
      showError("Informe o nome do arquivo.");
      return;
    }
    fd.append("nome", nome.trim());

    try {
      setQuickUploading(true);
      setQuickProgress(0);

      await api.post("ssw/releases/upload", fd, {
        ...AXIOS_UPLOAD_OPTS,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const pct = Math.round((evt.loaded * 100) / evt.total);
          setQuickProgress(pct);
        },
        headers: { "Content-Type": "multipart/form-data" },
      });

      showInfo("Release enviado para testes.");
      setQuickProgress(100);
      await fetchLatestRelease();
    } catch (e) {
      const aborted = e?.code === "ECONNABORTED";
      const msg =
        (aborted
          ? `Tempo excedido (${UPLOAD_TIMEOUT_MS / 1000}s).`
          : e?.response?.data?.error) ||
        e?.message ||
        "Falha ao publicar release.";
      showError(msg);
    } finally {
      setQuickUploading(false);
      setQuickProgress(0);
      if (quickFileRef.current) quickFileRef.current.value = "";
    }
  };

  const onQuickFileChange = (e) => doQuickUpload(e.target.files?.[0]);

  const isRouteActive = (path) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const goMobile = (path) => {
    setMobileMoreOpen(false);
    setSubmenuOpen(null);
    navigate(path);
  };

  const mobileBadge = (value, blink = false) => {
    const total = Number(value || 0);
    if (total <= 0) return null;

    return (
      <span className={`mobile-nav-badge ${blink ? "sb-blink" : ""}`}>
        {total > 99 ? "99+" : total}
      </span>
    );
  };

  const sidebarPrimaryItems = [
    {
      key: "dashboard",
      label: "Dashboard",
      mobileLabel: "Início",
      path: "/dashboard",
      icon: <FiHome />,
      visible: isADM,
    },
    {
      key: "chamados",
      label: "Chamados",
      path: "/chamados",
      icon: <FiList />,
      visible: true,
    },
    {
      key: "agendamentos",
      label: "Agendamentos",
      mobileLabel: "Agenda",
      path: "/agendamentos",
      icon: <FiCalendar />,
      badge: agBadge,
      blink: agBlink && !agBlinkMuted,
      visible: true,
    },
  ].filter((item) => item.visible !== false);

  const sidebarSections = [
    {
      key: "cadastros",
      label: "Cadastros",
      icon: <FiUser />,
      anchorRef: cadAnchorRef,
      items: [
        {
          key: "cad-clientes",
          label: "Clientes",
          path: "/cadastros",
          exact: true,
          icon: <FiUser />,
        },
        {
          key: "servicos",
          label: "Serviços Adicionais",
          path: "/servicos",
          icon: <FiTool />,
        },
        {
          key: "cad-func",
          label: "Funcionários",
          path: "/cadastros/funcionarios",
          icon: <FiUser />,
          visible: isADM,
        },
        {
          key: "cad-checklist",
          label: "Templates de Checklist",
          path: "/cadastroChecklist",
          icon: <FiList />,
          visible: isADM,
        },
      ],
    },
    {
      key: "projetos-operacoes",
      label: "Projetos e Operações",
      icon: <FiTool />,
      anchorRef: projectsAnchorRef,
      badge:
        Number(projBadge || 0) +
        (resumoErros.total > 0 && !errBadgeHidden
          ? Number(resumoErros.total)
          : 0),
      blink: projBlink,
      items: [
        {
          key: "minha-fila",
          label: "Minha Fila",
          path: "/minhaFila",
          icon: <FiList />,
          badge: projBadge,
          blink: projBlink,
        },
        {
          key: "solicitar-projeto",
          label: "Solicitar Projeto",
          path: "/solicitarProjeto",
          icon: <FiMessageSquare />,
        },
        {
          key: "projetos",
          label: "Projetos",
          path: "/projetos",
          icon: <FiTool />,
          visible: isADM,
        },
        {
          key: "gestao-projetos",
          label: "Gestão de Projetos",
          path: "/gestaoProjetos",
          icon: <FiTool />,
          visible: isADM,
        },
        {
          key: "exec-checklist",
          label: "Execução de Checklists",
          path: "/execucaoChecklist",
          icon: <FiList />,
        },
        {
          key: "aprovar-checklist",
          label: "Aprovação de Checklists",
          path: "/aprovarChecklist",
          icon: <FiList />,
          visible: isADM,
        },
        {
          key: "erros",
          label: "Gestão de Erros",
          path: "/erros",
          icon: <FiAlertTriangle />,
          badge:
            resumoErros.total > 0 && !errBadgeHidden
              ? resumoErros.total
              : 0,
          badgeAction: {
            label: errBadgeHidden ? "👁️‍🗨️" : "🙈",
            title: errBadgeHidden
              ? "Mostrar badge de erros"
              : "Ocultar badge de erros",
            onClick: toggleErrVisibility,
          },
        },
      ],
    },
    {
      key: "fiscal-tributario",
      label: "Fiscal e Tributário",
      icon: <FiRepeat />,
      anchorRef: fiscalAnchorRef,
      items: [
        {
          key: "regras",
          label: "Regras de Imposto",
          path: "/regras-de-imposto",
          icon: <FiRepeat />,
        },
        {
          key: "ibpt",
          label: "IBPT",
          path: "/ibpt",
          icon: <FiUpload />,
        },
        {
          key: "fiscal-nfe",
          label: "Emissão de NFS-e",
          path: "/fiscal/nfe",
          icon: <FiFileText />,
          visible: canSeeFiscalNfe,
        },
        {
          key: "fiscal-nfe",
          label: "Saneamento Fiscal",
          path: "/SaneamentoFiscal",
          icon: <FiFileText />,
          visible: canSeeFiscalNfe,
        },
      ],
    },
    {
      key: "financeiro",
      label: "Financeiro",
      icon: <FiDollarSign />,
      anchorRef: finAnchorRef,
      visible: canSeeFinanceiro,
      items: [
        {
          key: "finance-dash",
          label: "Dashboard Financeiro",
          path: "/financeiro/dashboard",
          icon: <FiBarChart2 />,
          visible: canSeeContasPagar,
        },
        {
          key: "cobranca",
          label: "Cobrança",
          path: "/financeiro/cobranca",
          icon: <FiDollarSign />,
          visible: canSeeCobranca,
        },
        {
          key: "contas-pagar",
          label: "Contas a Pagar",
          path: "/financeiro/contas-pagar",
          icon: <FiDollarSign />,
          visible: canSeeContasPagar,
        },
      ],
    },
    {
      key: "comunicacao",
      label: "Comunicação",
      icon: <FiMessageSquare />,
      anchorRef: communicationAnchorRef,
      badge:
        Number(totalMensagensPend || msgBadgeCtx || 0) +
        Number(totalAgendaPend || agendaBadgeCtx || 0),
      items: [
        {
          key: "mensagens-internas",
          label: "Mensagens Internas",
          path: "/mensagens-internas",
          icon: <FiMessageSquare />,
          badge: totalMensagensPend || msgBadgeCtx,
          onClick: handleVerMensagens,
        },
        {
          key: "agenda-lembretes",
          label: "Agenda e Lembretes",
          path: "/agenda",
          icon: <FiCalendar />,
          badge: totalAgendaPend || agendaBadgeCtx,
          onClick: handleVerAgenda,
        },
        {
          key: "whatsapp-dashboard",
          label: "Dashboard WhatsApp",
          path: "/financeiro/dashboardWhatsApp",
          icon: <FiBarChart2 />,
          visible: canSeeDashboardWhatsApp,
        },
        {
          key: "leads",
          label: "Leads",
          path: "/leads",
          icon: <FiMessageSquare />,
          visible: isMarketing,
        },
      ],
    },
    {
      key: "administracao",
      label: "Administração",
      icon: <FiTool />,
      anchorRef: adminAnchorRef,
      visible: isADM || canSeeContasPagar,
      items: [
        {
          key: "transferencia",
          label: "Transferência de Dados",
          path: "/transferenciaDeDados",
          icon: <FiRepeat />,
          visible: isADM,
        },
        {
          key: "gestao-ponto",
          label: "Gestão de Ponto",
          path: "/gestao/ponto",
          icon: <FiClock />,
          visible: canSeeContasPagar,
        },
        {
          key: "releases",
          label: "Releases em Teste",
          path: "/releases/testes",
          icon: <FiDownload />,
          visible: isADM,
        },
        {
          key: "mensagens-legado",
          label: "Mensagens (legado)",
          path: "/mensagens",
          icon: <FiMessageSquare />,
          visible: isADM,
        },
      ],
    },
  ];

  const getVisibleSectionItems = (section) =>
    (section?.items || []).filter((item) => item.visible !== false);

  const isSidebarItemActive = (item) => {
    if (!item?.path) return false;
    if (item.exact) return location.pathname === item.path;
    return isRouteActive(item.path);
  };

  const isSidebarSectionActive = (section) =>
    submenuOpen === section.key ||
    getVisibleSectionItems(section).some(isSidebarItemActive);

  const handleSidebarItemClick = (item, closeMobile = false) => {
    setSubmenuOpen(null);
    if (closeMobile) setMobileMoreOpen(false);

    if (typeof item?.onClick === "function") {
      item.onClick();
      return;
    }

    if (item?.path) navigate(item.path);
  };

  const renderDesktopSubmenuItem = (item) => {
    const badge = Number(item.badge || 0);

    return (
      <button
        key={item.key || item.label}
        type="button"
        className={`submenu-btn ${
          isSidebarItemActive(item) ? "active" : ""
        }`}
        onClick={() => handleSidebarItemClick(item)}
        aria-current={isSidebarItemActive(item) ? "page" : undefined}
      >
        <span className="submenu-item-icon">{item.icon}</span>
        <span className="submenu-item-label">{item.label}</span>

        {badge > 0 && (
          <span className={`badge-total ${item.blink ? "sb-blink" : ""}`}>
            {badge > 99 ? "99+" : badge}
          </span>
        )}

        {item.badgeAction && (
          <span
            className="badge-action"
            title={item.badgeAction.title}
            onClick={(e) => {
              e.stopPropagation();
              item.badgeAction.onClick?.();
            }}
          >
            {item.badgeAction.label}
          </span>
        )}
      </button>
    );
  };

  const mobilePrimaryItems = [
    ...sidebarPrimaryItems.map((item) => ({
      ...item,
      label: item.mobileLabel || item.label,
    })),
    {
      key: "cadastros",
      label: "Clientes",
      path: "/cadastros",
      icon: <FiUser />,
      visible: true,
    },
  ].filter((item) => item.visible !== false);

  const renderMobileSheetItem = (item) => {
    if (item.visible === false) return null;

    const active = isSidebarItemActive(item);
    const badge = Number(item.badge || 0);

    return (
      <button
        key={item.key || item.label}
        type="button"
        className={`mobile-sheet-item ${active ? "active" : ""} ${
          item.danger ? "danger" : ""
        }`}
        onClick={() => handleSidebarItemClick(item, true)}
        aria-current={active ? "page" : undefined}
      >
        <span className="mobile-sheet-icon">{item.icon}</span>
        <span className="mobile-sheet-label">{item.label}</span>
        {mobileBadge(badge, item.blink)}
      </button>
    );
  };

  const mobileSections = [
    ...sidebarSections,
    {
      key: "area-pessoal",
      title: "Área pessoal",
      label: "Área pessoal",
      items: [
        {
          key: "ponto",
          label: "Meu Cartão de Ponto",
          path: "/ponto",
          icon: <FiClock />,
        },
        {
          key: "sair",
          label: "Sair",
          icon: <FiLogOut />,
          danger: true,
          onClick: handleLogout,
        },
      ],
    },
  ];

  if (isMobile) {
    return (
      <>
        <nav className="mobile-bottom-nav" aria-label="Navegação principal">
          {mobilePrimaryItems.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`mobile-nav-item ${
                isRouteActive(item.path) ? "active" : ""
              }`}
              onClick={() => goMobile(item.path)}
              aria-current={isRouteActive(item.path) ? "page" : undefined}
            >
              <span className="mobile-nav-icon">{item.icon}</span>
              <span className="mobile-nav-label">{item.label}</span>
              {mobileBadge(item.badge, item.blink)}
            </button>
          ))}

          <button
            type="button"
            className={`mobile-nav-item mobile-nav-more ${
              mobileMoreOpen ? "active" : ""
            }`}
            onClick={() => setMobileMoreOpen((v) => !v)}
            aria-expanded={mobileMoreOpen}
            aria-label="Abrir mais opções"
          >
            <span className="mobile-nav-icon">
              <FiMoreHorizontal />
            </span>
            <span className="mobile-nav-label">Mais</span>
            {mobileBadge(
              (resumoErros.total > 0 && !errBadgeHidden ? resumoErros.total : 0) +
                Number(totalMensagensPend || 0) +
                Number(totalAgendaPend || 0) +
                Number(projBadge || 0),
              projBlink
            )}
          </button>
        </nav>

        {mobileMoreOpen && (
          <div className="mobile-more-layer" role="presentation">
            <button
              type="button"
              className="mobile-more-backdrop"
              aria-label="Fechar menu"
              onClick={() => setMobileMoreOpen(false)}
            />

            <section
              className="mobile-more-sheet"
              role="dialog"
              aria-modal="true"
              aria-label="Mais opções"
            >
              <div className="mobile-sheet-handle" />

              <header className="mobile-sheet-header">
                <div>
                  <span className="mobile-sheet-eyebrow">SantSystem Web 3.0</span>
                  <strong>{userName || "Usuário"}</strong>
                  <small>Variação diária: {zNumero || "—"}</small>
                </div>

                <button
                  type="button"
                  className="mobile-sheet-close"
                  onClick={() => setMobileMoreOpen(false)}
                  aria-label="Fechar menu"
                >
                  <FiX />
                </button>
              </header>

              {(agBadge > 0 || typeof filaTotal === "number") && (
                <div className="mobile-sheet-summary">
                  {agBadge > 0 && (
                    <button
                      type="button"
                      className={`mobile-summary-card danger ${
                        agBlink && !agBlinkMuted ? "sb-blink" : ""
                      }`}
                      onClick={() => goMobile("/agendamentos")}
                    >
                      <span>Agendamentos hoje</span>
                      <strong>{agBadge}</strong>
                    </button>
                  )}

                  {typeof filaTotal === "number" && (
                    <button
                      type="button"
                      className="mobile-summary-card neutral"
                      onClick={() => goMobile("/minhaFila")}
                    >
                      <span>Projetos na fila</span>
                      <strong>{filaTotal}</strong>
                    </button>
                  )}
                </div>
              )}

              <div className="mobile-sheet-content">
                {mobileSections.map((section) => {
                  if (section.visible === false) return null;

                  const items = getVisibleSectionItems(section);

                  if (!items.length) return null;

                  return (
                    <div className="mobile-sheet-section" key={section.key || section.title}>
                      <h4>{section.title || section.label}</h4>
                      <div className="mobile-sheet-grid">
                        {items.map((item) => renderMobileSheetItem(item))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        )}

        {USE_MODAL && (
          <ReleaseUploadModal
            open={uploadOpen}
            onClose={() => setUploadOpen(false)}
            onSuccess={() => fetchLatestRelease()}
            produtoCfg={produtoCfg}
            produtoRelease={produtoRelease}
          />
        )}

        <PontoReminderModal
          open={
            podeExibirLembretePonto &&
            showPontoReminderModal &&
            !!alertaPrioritario
          }
          alerta={alertaPrioritario}
          registrando={!!registrandoCampo}
          onClose={handleClosePontoReminder}
          onRemindLater={handleRemindLaterPontoReminder}
          onRegistrar={handleRegistrarPonto}
        />
      </>
    );
  }

  return (
    <>
      <div className={`sidebar ${isOpen ? "open" : "collapsed"}`}>
        {/* Topo */}
        <div className="top-section">
          <img src={isOpen ? Logo : LogoCurto} alt="Logo" className="logo" />

          <button
            className={`toggle-btn ${lockCollapsed ? "disabled" : ""}`}
            onClick={toggleSidebar}
            title={
              lockCollapsed
                ? "Sidebar travado em telas pequenas"
                : isOpen
                ? "Recolher"
                : "Expandir"
            }
            disabled={lockCollapsed}
            aria-label={
              isOpen ? "Recolher menu lateral" : "Expandir menu lateral"
            }
          >
            {isOpen ? <FiChevronLeft /> : <FiChevronRight />}
          </button>

          {!isOpen && (
            <span
              className="z-chip-collapsed"
              title="Variação Diária (Z-número)"
              aria-label="Variação Diária"
            >
              {zNumero || "—"}
            </span>
          )}
        </div>

        {/* Z (aberto) */}
        {isOpen && (
          <div className="znum-widget open">
            <span className="znum-label">Variação Diária</span>
            <span className="znum-value">{zNumero || "—"}</span>
          </div>
        )}

        {/* ===== ACESSO RÁPIDO DE DEPLOY / RELEASES ===== */}
        <div className="release-launcher-wrap" ref={releaseAnchorRef}>
          <input
            ref={quickFileRef}
            type="file"
            accept={produtoCfg.accept || "*"}
            onChange={onQuickFileChange}
            style={{ display: "none" }}
          />

          <button
            type="button"
            className={`release-launcher ${
              releasePanelOpen ? "active" : ""
            }`}
            onClick={() => setReleasePanelOpen((prev) => !prev)}
            aria-expanded={releasePanelOpen}
            aria-haspopup="dialog"
            title="Abrir painel lateral de deploy e releases"
          >
            {isOpen ? (
              <>
                <span className="release-launcher-title">
                  Deploy e Releases
                </span>

                <span className="release-launcher-status-row">
                  <span className="release-launcher-icon">
                    {quickUploading ? <FiUpload /> : <FiDownload />}

                    <span
                      className={`release-state-dot ${releaseConnectionClass}`}
                      title={releaseConnectionLabel}
                      aria-label={releaseConnectionLabel}
                    />
                  </span>

                  <FiChevronRight className="release-launcher-chevron" />
                </span>

                <span
                  className={`release-launcher-badge ${
                    releaseOnline ? releaseStatusClass : "offline"
                  }`}
                >
                  {releaseOnline ? releaseStatusLabel : "Offline"}
                </span>

                <span className="release-launcher-version">
                  {releaseVersionText}
                </span>
              </>
            ) : (
              <span className="release-launcher-icon">
                {quickUploading ? <FiUpload /> : <FiDownload />}

                <span
                  className={`release-state-dot ${releaseConnectionClass}`}
                  title={releaseConnectionLabel}
                  aria-label={releaseConnectionLabel}
                />
              </span>
            )}
          </button>
        </div>
        {/* ===== FIM ACESSO RÁPIDO DE DEPLOY / RELEASES ===== */}

        {/* Menu */}
        <div className="menu-items">
          {sidebarPrimaryItems.map((item) => {
            const active = isSidebarItemActive(item);
            const badge = Number(item.badge || 0);

            return (
              <button
                key={item.key}
                type="button"
                className={`menu-btn ${active ? "active" : ""}`}
                onClick={() => handleSidebarItemClick(item)}
                aria-current={active ? "page" : undefined}
                title={!isOpen ? item.label : undefined}
              >
                {item.icon}
                {isOpen && <span>{item.label}</span>}

                {badge > 0 && (
                  <span
                    className={`${isOpen ? "badge-total" : "badge-dot"} ${
                      item.blink ? "sb-blink" : ""
                    }`}
                    title={`${badge} pendência(s)`}
                  >
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </button>
            );
          })}

          {sidebarSections.map((section) => {
            if (section.visible === false) return null;

            const items = getVisibleSectionItems(section);
            if (!items.length) return null;

            const active = isSidebarSectionActive(section);
            const badge = Number(section.badge || 0);

            return (
              <div
                className="submenu-wrapper"
                ref={section.anchorRef}
                key={section.key}
              >
                <button
                  type="button"
                  className={`menu-btn ${active ? "active" : ""}`}
                  onClick={() =>
                    setSubmenuOpen((current) =>
                      current === section.key ? null : section.key
                    )
                  }
                  title={!isOpen ? section.label : undefined}
                  aria-expanded={submenuOpen === section.key}
                >
                  {section.icon}

                  {isOpen && (
                    <>
                      <span className="menu-section-label">{section.label}</span>

                      {badge > 0 && (
                        <span
                          className={`menu-section-badge ${
                            section.blink ? "sb-blink" : ""
                          }`}
                        >
                          {badge > 99 ? "99+" : badge}
                        </span>
                      )}

                      <span
                        className={`arrow ${
                          submenuOpen === section.key ? "rotated" : ""
                        }`}
                      >
                        ▾
                      </span>
                    </>
                  )}

                  {!isOpen && badge > 0 && (
                    <span
                      className={`badge-dot ${
                        section.blink ? "sb-blink" : ""
                      }`}
                      title={`${badge} pendência(s)`}
                    >
                      {badge > 99 ? "99+" : badge}
                    </span>
                  )}
                </button>

                {isOpen && submenuOpen === section.key && (
                  <div className="submenu-items">
                    {items.map(renderDesktopSubmenuItem)}
                  </div>
                )}
              </div>
            );
          })}

          <button
            type="button"
            className={`menu-btn ${
              isRouteActive("/ponto") ? "active" : ""
            }`}
            onClick={() => navigate("/ponto")}
            aria-current={isRouteActive("/ponto") ? "page" : undefined}
            title={!isOpen ? "Meu Cartão de Ponto" : undefined}
          >
            <FiClock />
            {isOpen && <span>Meu Cartão de Ponto</span>}
          </button>
        </div>

        {/* ===== Alert Center (compacto) ===== */}
        {(agBadge > 0 || typeof filaTotal === "number") && (
          <div className="mini-alert-center">
            {agBadge > 0 && (
              <div
                className={`alc-item danger ${
                  agBlink && !agBlinkMuted ? "sb-blink" : ""
                }`}
                title="Novos agendamentos de HOJE (pendentes) após sua última consulta"
              >
                <div className="alc-title">Agendamentos de hoje</div>
                <div className="alc-meta">
                  <span className="alc-pill">{agBadge} novo(s)</span>
                </div>
                <div className="alc-actions">
                  <button
                    className="alc-btn"
                    onClick={() => navigate("/agendamentos")}
                  >
                    Ver
                  </button>
                  <button className="alc-btn" onClick={ackAgToday}>
                    Marcar como visto
                  </button>
                  <button className="alc-btn" onClick={toggleAgBlink}>
                    {agBlinkMuted ? "Piscar: off" : "Piscar: on"}
                  </button>
                </div>
              </div>
            )}

            {typeof filaTotal === "number" && (
              <div
                className="alc-line neutral"
                title="Total de projetos na sua fila (exclui concluídos)"
              >
                <span className="alc-line-text">
                  Projetos na sua Fila: <strong>{filaTotal}</strong>
                </span>
                <div className="alc-actions">
                  <button
                    className="alc-btn"
                    onClick={() => navigate("/minhaFila")}
                  >
                    Abrir
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== Avisos Comunicação Interna (Mensagens / Agenda) ===== */}
        {(totalMensagensPend > 0 || totalAgendaPend > 0) && (
          <div className="mini-alert-center mini-alert-center--comms">
            {totalMensagensPend > 0 && (
              <div
                className="alc-item neutral"
                title="Mensagens internas não lidas / recentes"
              >
                <div className="alc-title">Mensagens internas</div>
                <div className="alc-meta">
                  <span className="alc-pill">{totalMensagensPend} nova(s)</span>
                </div>
                <div className="alc-actions">
                  <button
                    className="alc-btn"
                    type="button"
                    onClick={handleVerMensagens}
                  >
                    Abrir
                  </button>
                  <button
                    className="alc-btn"
                    type="button"
                    onClick={handleDescartarMensagens}
                  >
                    Marcar como lidas
                  </button>
                </div>
              </div>
            )}

            {totalAgendaPend > 0 && (
              <div
                className="alc-item neutral"
                title="Lembretes e eventos próximos do vencimento"
              >
                <div className="alc-title">Agenda / Lembretes</div>
                <div className="alc-meta">
                  <span className="alc-pill">
                    {totalAgendaPend} pendente(s)
                  </span>
                </div>
                <div className="alc-actions">
                  <button
                    className="alc-btn"
                    type="button"
                    onClick={handleVerAgenda}
                  >
                    Abrir
                  </button>
                  <button
                    className="alc-btn"
                    type="button"
                    onClick={handleDescartarAgenda}
                  >
                    Marcar como vistas
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Alertas de ponto */}
        {podeExibirLembretePonto && alertas.length > 0 && (
          <div className="ponto-alerta">
            {alertas.map((a, i) => (
              <div key={i} className="alerta-box">
                <p>
                  {`Está na hora de registrar: ${a.label}${
                    a.previsto ? ` (${a.previsto})` : ""
                  }`}
                </p>
                <button
                  type="button"
                  className="btn-alerta"
                  onClick={() => handleRegistrarPonto(a.campo)}
                  disabled={!!registrandoCampo}
                  aria-busy={registrandoCampo === a.campo ? "true" : "false"}
                >
                  <FiClock />
                  {registrandoCampo === a.campo
                    ? "Registrando..."
                    : `Registrar ${formatCampoLabel(a.campo)}`}
                </button>
              </div>
            ))}
          </div>
        )}

        {podeExibirLembretePonto && !isOpen && alertas.length > 0 && (
          <div className="alert-icon-collapsed" title="Você tem ponto pendente">
            ⚠️
          </div>
        )}

        {/* Mini-widget de erros */}
        {isOpen && resumoErros.total > 0 && !errBadgeHidden && (
          <div className="mini-widget-erros has-data" aria-live="polite">
            <div className="mw-title">
              <FiAlertTriangle />
              <span>Erros no sistema</span>
              <button
                className="mw-hide"
                title="Ocultar este badge"
                onClick={toggleErrVisibility}
              >
                ocultar
              </button>
            </div>
            <div className="mw-metrics">
              <span className="mw-chip open">
                Abertos: {resumoErros.abertos}
              </span>
              <span className="mw-chip wip">
                Em atend.: {resumoErros.emAtendimento}
              </span>
              <span className="mw-chip done">
                Finalizados: {resumoErros.finalizados}
              </span>
            </div>
            <button className="mw-btn" onClick={goErros}>
              Ver erros ({resumoErros.total})
            </button>
          </div>
        )}

        <div
          className={`user-card ${isOpen ? "open" : "collapsed"}`}
          title={userName || "Usuário"}
        >
          <FiUser />
          {isOpen && (
            <div className="user-card-text">
              <div className="uc-label">Usuário logado</div>
              <div className="uc-name">{userName || "—"}</div>
            </div>
          )}
        </div>

        <div className="logout-section">
          <button className="menu-btn logout-btn" onClick={handleLogout}>
            <FiLogOut />
            {isOpen && <span>Sair</span>}
          </button>
        </div>
      </div>

      {/* ==== POPOVERS (fora da sidebar) — somente quando COLAPSADO ==== */}
      {/* Painel lateral de Deploy / Releases */}
      <FloatSubmenu
        open={releasePanelOpen}
        anchorRef={releaseAnchorRef}
        onClose={() => setReleasePanelOpen(false)}
        width={390}
        className="release-side-panel"
        role="dialog"
        ariaLabel="Painel de deploy e releases"
      >
        <div className="release-side-panel__header">
          <div className="release-side-panel__title">
            <span className="release-side-panel__icon">
              <FiDownload />
            </span>

            <div>
              <span>Central operacional</span>
              <strong>Deploy e Releases</strong>
            </div>
          </div>

          <button
            type="button"
            className="release-side-panel__close"
            onClick={() => setReleasePanelOpen(false)}
            aria-label="Fechar painel de deploy"
          >
            <FiX />
          </button>
        </div>

        <div className="release-side-panel__toolbar">
          <label>
            <span>Produto</span>
            <select
              value={produtoRelease}
              onChange={(e) => setProdutoRelease(e.target.value)}
              className="release-side-panel__select"
            >
              {RELEASE_PRODUTOS.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            className="release-side-panel__upload"
            onClick={
              USE_MODAL ? () => setUploadOpen(true) : handleQuickFilePick
            }
            disabled={quickUploading}
          >
            <FiUpload />
            <span>
              {quickUploading
                ? `Enviando ${quickProgress || 0}%`
                : "Enviar release"}
            </span>
          </button>
        </div>

        <div className="release-side-panel__content">
          {relLoading ? (
            <div className="release-side-panel__loading">
              <div className="release-side-panel__skeleton" />
              <div className="release-side-panel__skeleton short" />
              <div className="release-side-panel__skeleton" />
            </div>
          ) : relErr ? (
            <div className="release-side-panel__message error">
              <FiAlertTriangle />
              <div>
                <strong>Falha ao carregar os releases</strong>
                <span>{relErr}</span>
              </div>
            </div>
          ) : !releaseLast ? (
            <div className="release-side-panel__message empty">
              <FiAlertTriangle />
              <div>
                <strong>Nenhum release disponível</strong>
                <span>
                  Envie um arquivo para iniciar o fluxo de homologação.
                </span>
              </div>
            </div>
          ) : (
            <>
              <div className="release-side-panel__summary">
                <div>
                  <span>Última versão</span>
                  <strong>{releaseVersao || "—"}</strong>
                  <small>{produtoCfg?.label || produtoRelease}</small>
                </div>

                <span
                  className={`release-side-panel__status ${releaseStatusClass}`}
                >
                  {releaseStatusLabel}
                </span>
              </div>

              <div className="release-side-panel__grid">
                <div>
                  <span>Autor</span>
                  <strong>{releaseAutor || "—"}</strong>
                </div>

                <div>
                  <span>Ambiente</span>
                  <strong>{lastAtiva ? "Produção" : "Homologação"}</strong>
                </div>

                <div className="full">
                  <span>Enviado em</span>
                  <strong>{formatWhen(releaseUploadedAt)}</strong>
                </div>

                <div>
                  <span>Tamanho</span>
                  <strong>{formatBytes(releaseFileSize)}</strong>
                </div>

                <div>
                  <span>Status técnico</span>
                  <strong>{lastStatus || "EM TESTE"}</strong>
                </div>
              </div>

              {releaseSha256 && (
                <button
                  type="button"
                  className="release-side-panel__hash"
                  onClick={() => {
                    navigator.clipboard.writeText(releaseSha256);
                    showInfo("SHA-256 copiado.");
                  }}
                  title="Copiar SHA-256"
                >
                  <FiHash />
                  <span>
                    <small>SHA-256</small>
                    <strong>{releaseSha256}</strong>
                  </span>
                </button>
              )}
            </>
          )}
        </div>

        <div className="release-side-panel__actions">
          <button
            type="button"
            className="primary"
            onClick={openDownload}
            disabled={!lastAtiva || quickUploading}
          >
            <FiDownload />
            <span>
              {lastAtiva
                ? "Baixar produção"
                : lastStatus === "APROVADO"
                ? "Aguardando ativação"
                : "Em homologação"}
            </span>
          </button>

          <button type="button" className="secondary" onClick={goReleaseTestes}>
            <FiTool />
            <span>Gerenciar releases</span>
          </button>
        </div>
      </FloatSubmenu>

      {sidebarSections.map((section) => {
        if (section.visible === false) return null;

        const items = getVisibleSectionItems(section);
        if (!items.length) return null;

        return (
          <FloatSubmenu
            key={`float-${section.key}`}
            open={!isOpen && submenuOpen === section.key}
            anchorRef={section.anchorRef}
            onClose={() => setSubmenuOpen(null)}
            ariaLabel={section.label}
          >
            <div className="pop-submenu-title">
              <span className="pop-submenu-title-icon">{section.icon}</span>
              <strong>{section.label}</strong>
            </div>

            {items.map(renderDesktopSubmenuItem)}
          </FloatSubmenu>
        );
      })}

      {/* Modal de Upload de Release (opcional) */}
      {USE_MODAL && (
        <ReleaseUploadModal
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          onSuccess={() => fetchLatestRelease()}
          produtoCfg={produtoCfg}
          produtoRelease={produtoRelease}
        />
      )}

      {/* Modal forte de lembrete de ponto */}
      <PontoReminderModal
        open={podeExibirLembretePonto && showPontoReminderModal && !!alertaPrioritario}
        alerta={alertaPrioritario}
        registrando={!!registrandoCampo}
        onClose={handleClosePontoReminder}
        onRemindLater={handleRemindLaterPontoReminder}
        onRegistrar={handleRegistrarPonto}
      />
    </>
  );
}
